import { ChangeDetectionStrategy, Component, ElementRef, inject, input, OnDestroy } from '@angular/core';
import { DfEventService } from '@df/core/service/df-event';
import { BlockStickyService } from '@df/ui/block-sticky.service';
import { BodyClassService } from '@df/ui/body-class.service';
import { UiComponent } from '@df/ui/ui.component';
import { ScrollService } from '@df/util/service/scroll.service';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { DIALOG_EVENT, DialogEventType, type DialogPosition } from './dialog.interface';

@Component({
    selector: 'dialog',
    exportAs: 'dialog',
    templateUrl: './dialog.component.html',
    styleUrl: './dialog.component.scss',
    host: {
        '[class._left-side]': 'side() === "left"',
        '[class._right-side]': 'side() === "right"',
        '[class._center-side]': 'side() === "center"'
    },
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DialogComponent extends UiComponent implements OnDestroy {
    id = input.required<string>();
    side = input<DialogPosition>('left');
    blockScroll = input(true);
    stickyOff = input(false);

    isVisible$ = new BehaviorSubject<boolean>(false);

    private scrollRelease?: () => void;

    private readonly elementRef = inject(ElementRef<HTMLDialogElement>);
    private readonly dfEventService = inject(DfEventService);
    private readonly scrollService = inject(ScrollService);
    private readonly blockStickyService = inject(BlockStickyService);
    private readonly bodyClassService = inject(BodyClassService);

    constructor() {
        super();

        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (event.id === DIALOG_EVENT && event.data.id === this.id()) {
                    if (event.data.type === DialogEventType.Open) {
                        this.showModal();
                    } else {
                        this.close();
                    }
                }
            })
        );
    }

    get element(): HTMLDialogElement {
        return this.elementRef.nativeElement;
    }

    get isOpen(): boolean {
        return this.elementRef?.nativeElement.open ?? false;
    }

    override ngOnDestroy() {
        this.releaseScroll();
        this.bodyClassService.remove('dialog-open', this);

        super.ngOnDestroy();
    }

    close() {
        if (this.isVisible$.value) {
            this.element.close();

            this.isVisible$.next(false);

            this.releaseScroll();

            this.bodyClassService.remove('dialog-open', this);
        }
    }

    showModal() {
        if (!this.isVisible$.value) {
            this.element.show();

            this.isVisible$.next(true)

            if (this.blockScroll()) {
                this.scrollRelease = this.scrollService.blockScroll();
            }

            if (this.stickyOff()) {
                this.blockStickyService.disallow(this);
            }

            this.bodyClassService.add('dialog-open', this);
        }
    }

    private releaseScroll() {
        if (this.scrollRelease) {
            this.scrollRelease();
            this.scrollRelease = undefined;
        }

        this.blockStickyService.allow(this);
    }
}
